/**
 * Tabbed Products — Web Component
 *
 * Handles tab switching, ARIA state, keyboard navigation,
 * and dispatches resize events for carousels in newly-visible panels.
 *
 * Scroll jump prevention:
 * When hiding a panel (display:none), page height can shrink below the current
 * scroll position, causing the browser to clamp scrollY. Fix: lock the wrapper's
 * min-height before the switch, show the new panel before hiding the old one,
 * and release the height lock after layout settles.
 *
 * iOS WebKit compositing layer fix:
 * iOS discards GPU compositing layers for elements inside display:none containers.
 * When a panel is revealed, slideshow-slides has correct dimensions but renders
 * blank because no compositing layer exists yet. Fix: reset the scroll position
 * to slide 0, then apply and immediately remove translateZ(0) to force iOS to
 * promote a new layer and repaint. This must happen synchronously after unhiding,
 * before the browser yields to the next frame.
 */
if (!customElements.get('tabbed-products')) {
  class TabbedProducts extends HTMLElement {
    constructor() {
      super();
      this.tabs = [];
      this.panels = [];
      this.activeIndex = 0;
    }

    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));

      if (this.tabs.length === 0) return;

      this.tabBar = this.querySelector('[role="tablist"]');

      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.switchTab(index, false));
      });

      if (this.tabBar) {
        this.tabBar.addEventListener('keydown', (event) => this.handleKeydown(event));
      }
    }

    /**
     * Force iOS WebKit to repaint a slideshow-slides element.
     *
     * iOS discards compositing layers when an element is inside a display:none
     * container. Simply removing `hidden` does not recreate the layer — the
     * element renders blank until a touch event triggers a GPU repaint.
     *
     * Applying translateZ(0) synchronously promotes the element to its own
     * compositing layer and forces an immediate repaint. Removing it in the
     * next frame tears down the unnecessary layer without visual artefact.
     *
     * @param {HTMLElement} panel - The newly visible tab panel
     */
    repaintCarousel(panel) {
      const slideshowSlides = panel.querySelector('slideshow-slides');
      if (!slideshowSlides) return;

      // Reset to slide 0 — stale scroll offsets from a previous display:none
      // state can place the carousel viewport between slides on reveal
      slideshowSlides.scrollLeft = 0;

      // Force compositing layer promotion → triggers iOS GPU repaint
      slideshowSlides.style.webkitTransform = 'translateZ(0)';
      slideshowSlides.style.transform = 'translateZ(0)';

      // Remove the layer hint one frame later — it has done its job
      requestAnimationFrame(() => {
        slideshowSlides.style.webkitTransform = '';
        slideshowSlides.style.transform = '';
      });
    }

    /**
     * Switch to a tab by index.
     * @param {number} index — tab index to activate
     * @param {boolean} fromKeyboard — true when triggered by keyboard (focuses the tab)
     */
    switchTab(index, fromKeyboard) {
      if (index < 0 || index >= this.tabs.length || index === this.activeIndex) return;

      // Lock wrapper min-height so page height never shrinks during transition
      this.style.minHeight = this.offsetHeight + 'px';

      // Show new panel FIRST (both momentarily visible) to prevent height collapse
      const nextTab = this.tabs[index];
      const nextPanel = this.panels[index];

      if (nextPanel) {
        nextPanel.classList.add('section-tabbed-products__panel--active');
        nextPanel.removeAttribute('hidden');

        // Force iOS WebKit to recreate the compositing layer and repaint.
        // Must be called synchronously here — before the browser yields to the
        // next frame — so the layer is promoted before any paint pass occurs.
        this.repaintCarousel(nextPanel);
      }

      // Then hide old panel
      const prevTab = this.tabs[this.activeIndex];
      const prevPanel = this.panels[this.activeIndex];

      if (prevPanel) {
        prevPanel.classList.remove('section-tabbed-products__panel--active');
        prevPanel.setAttribute('hidden', '');
      }

      // Update tab ARIA and active states
      if (prevTab) {
        prevTab.classList.remove('section-tabbed-products__tab--active');
        prevTab.setAttribute('aria-selected', 'false');
        prevTab.setAttribute('tabindex', '-1');
      }

      if (nextTab) {
        nextTab.classList.add('section-tabbed-products__tab--active');
        nextTab.setAttribute('aria-selected', 'true');
        nextTab.setAttribute('tabindex', '0');

        // Only programmatically focus on keyboard navigation
        if (fromKeyboard) {
          nextTab.focus({ preventScroll: true });
        }
      }

      this.activeIndex = index;

      // Notify carousels that layout changed, then release height lock
      requestAnimationFrame(() => {
        const scrollY = window.scrollY;
        window.dispatchEvent(new Event('resize'));

        requestAnimationFrame(() => {
          // Restore scroll position in case resize shifted it
          window.scrollTo(0, scrollY);
          // Release the height lock
          this.style.minHeight = '';
          // Scroll active tab into view within the tab bar
          if (nextTab) {
            this.scrollTabIntoView(nextTab);
          }
        });
      });
    }

    /**
     * Smoothly scroll the active tab into view within the tab bar.
     */
    scrollTabIntoView(tab) {
      if (!this.tabBar) return;

      const barRect = this.tabBar.getBoundingClientRect();
      const tabRect = tab.getBoundingClientRect();

      const isFullyVisible = tabRect.left >= barRect.left && tabRect.right <= barRect.right;

      if (!isFullyVisible) {
        const scrollLeft = tab.offsetLeft - this.tabBar.offsetLeft - (barRect.width / 2) + (tabRect.width / 2);
        this.tabBar.scrollTo({
          left: scrollLeft,
          behavior: 'smooth'
        });
      }
    }

    /**
     * Arrow-key navigation — cycles through all tabs.
     */
    handleKeydown(event) {
      const { key } = event;
      const count = this.tabs.length;
      if (count === 0) return;

      let targetIndex = this.activeIndex;

      switch (key) {
        case 'ArrowRight':
        case 'ArrowDown':
          targetIndex = (this.activeIndex + 1) % count;
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          targetIndex = (this.activeIndex - 1 + count) % count;
          break;
        case 'Home':
          targetIndex = 0;
          break;
        case 'End':
          targetIndex = count - 1;
          break;
        default:
          return;
      }

      event.preventDefault();
      this.switchTab(targetIndex, true);
    }
  }

  customElements.define('tabbed-products', TabbedProducts);
}